
import { v4 as uuidv4 } from 'uuid';
import { Industry } from '@/types/assessment';
import { AnalyticsMetric } from '@/types/assessment/analytics';
import { 
  calculateMarketPosition, 
  calculateAIReadiness, 
  calculateIndustryRank,
  determineTrend,
  calculatePercentageChange
} from '@/utils/analytics-calculations';
import { PreviousAssessment } from './assessmentHistoryService';

/**
 * Generates metrics based on assessment data and previous assessment for comparison
 */
export const generateMetrics = (
  dimensionScores: Record<string, number>, 
  overallScore: number,
  industry: Industry,
  previousAssessment: PreviousAssessment | null,
  currentOrganizationName: string
): AnalyticsMetric[] => {
  const calculateChange = (current: number, dimension?: string) => {
    // Only calculate change if previous assessment exists AND it's for the same organization
    if (!previousAssessment) {
      return undefined;
    }
    
    // Compare organization names in a case-insensitive manner
    const prevOrgName = previousAssessment.organizationName.trim().toLowerCase();
    const currentOrgName = currentOrganizationName.trim().toLowerCase();
    
    if (prevOrgName !== currentOrgName) {
      console.log(`Organization names don't match: current=${currentOrgName}, previous=${prevOrgName}`);
      return undefined;
    }
    
    let previous = dimension 
      ? previousAssessment.scores[dimension] 
      : previousAssessment.overallScore;
    
    // Avoid division by zero
    if (previous === 0) {
      return current > 0 ? 100 : 0; // If current has value but previous was 0, show 100% increase
    }
    
    return calculatePercentageChange(current, previous);
  };

  return [
    {
      id: uuidv4(),
      name: "Overall Maturity",
      value: overallScore,
      change: calculateChange(overallScore),
      trend: determineTrend(calculateChange(overallScore))
    },
    {
      id: uuidv4(),
      name: "Market Position",
      value: calculateMarketPosition(dimensionScores, industry),
      change: calculateChange(calculateMarketPosition(dimensionScores, industry)),
      trend: determineTrend(calculateChange(calculateMarketPosition(dimensionScores, industry)))
    },
    {
      id: uuidv4(),
      name: "AI Readiness",
      value: calculateAIReadiness(dimensionScores),
      change: calculateChange(calculateAIReadiness(dimensionScores)),
      trend: determineTrend(calculateChange(calculateAIReadiness(dimensionScores)))
    },
    {
      id: uuidv4(),
      name: "Industry Rank",
      value: calculateIndustryRank(overallScore, industry),
      change: previousAssessment ? 
        calculateIndustryRank(previousAssessment.overallScore, industry) - calculateIndustryRank(overallScore, industry) : 
        undefined,
      trend: previousAssessment ? 
        (calculateIndustryRank(previousAssessment.overallScore, industry) - calculateIndustryRank(overallScore, industry) > 0 ? "up" : 
         calculateIndustryRank(previousAssessment.overallScore, industry) - calculateIndustryRank(overallScore, industry) < 0 ? "down" : "neutral") 
        : "neutral"
    },
    {
      id: uuidv4(),
      name: "Innovation Potential",
      value: dimensionScores["innovation"] || 0,
      change: calculateChange(dimensionScores["innovation"] || 0, "innovation"),
      trend: determineTrend(calculateChange(dimensionScores["innovation"] || 0, "innovation")),
      dimension: "innovation"
    },
    {
      id: uuidv4(),
      name: "Data Maturity",
      value: dimensionScores["data"] || 0,
      change: calculateChange(dimensionScores["data"] || 0, "data"),
      trend: determineTrend(calculateChange(dimensionScores["data"] || 0, "data")),
      dimension: "data"
    }
  ];
};
